import { Chip } from "@heroui/react";
import dayjs from "dayjs";
import { Pencil, Trash } from "lucide-react";
import { useMemo } from "react";

import { NamedTargetType, usePromotionTargetOptions } from "./target-picker";

import {
  DataTable,
  NamedOption,
  RowMenu,
  type DataGridColumn,
  type TablePagination,
} from "@/components/kit";
import { PromotionTargetType } from "@/enums/promotion";
import { Promotion } from "@/types";
import { formatDay } from "@/utils/date";
import {
  ORDER_PROMOTION_TYPES,
  PROMOTION_TARGET_LABEL,
  PROMOTION_TYPE_LABEL,
  PromotionStatus,
  promotionStatus,
  promotionValueText,
} from "@/utils/promotion";

const STATUS_CHIP: Record<
  PromotionStatus,
  { label: string; color: "success" | "accent" | "default" }
> = {
  live: { label: "Live", color: "success" },
  scheduled: { label: "Scheduled", color: "accent" },
  ended: { label: "Ended", color: "default" },
  inactive: { label: "Inactive", color: "default" },
};

const VISIBLE_TARGETS = 2;

const targetNames = (
  promotion: Promotion,
  options: Record<NamedTargetType, NamedOption[]>,
): string[] => {
  if (ORDER_PROMOTION_TYPES.includes(promotion.type)) return ["Whole order"];

  const productCount = promotion.targets.filter(
    ({ targetType }) => targetType === PromotionTargetType.PRODUCT,
  ).length;
  const named = promotion.targets.flatMap(({ targetType, targetId }) => {
    if (targetType === PromotionTargetType.ALL) return ["All products"];
    if (targetType === PromotionTargetType.PRODUCT) return [];

    const name = options[targetType].find(({ id }) => id === targetId)?.name;

    return [
      name ?? `Unknown ${PROMOTION_TARGET_LABEL[targetType].toLowerCase()}`,
    ];
  });

  return productCount > 0
    ? [...named, `${productCount} product${productCount === 1 ? "" : "s"}`]
    : named;
};

interface PromotionsTableProps {
  promotions: Promotion[];
  isLoading: boolean;
  pagination: TablePagination;
  onEdit: (promotion: Promotion) => void;
  onDelete: (promotion: Promotion) => void;
}

export const PromotionsTable = ({
  promotions,
  isLoading,
  pagination,
  onEdit,
  onDelete,
}: PromotionsTableProps) => {
  const options = usePromotionTargetOptions();

  const columns = useMemo<DataGridColumn<Promotion>[]>(() => {
    const now = dayjs();

    return [
      {
        id: "name",
        header: "Promotion",
        isRowHeader: true,
        minWidth: 220,
        cell: (promotion) => (
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate font-medium">{promotion.name}</span>
            <span className="text-muted truncate text-xs">
              <bdi dir="rtl" lang="ar">
                {promotion.nameAr}
              </bdi>
            </span>
          </div>
        ),
      },
      {
        id: "discount",
        header: "Discount",
        minWidth: 200,
        cell: (promotion) => (
          <div className="flex flex-col leading-tight">
            <span>{promotionValueText(promotion)}</span>
            <span className="text-muted text-xs">
              {PROMOTION_TYPE_LABEL[promotion.type]}
              {promotion.minimumSubtotal !== null
                ? ` · min AED ${promotion.minimumSubtotal.toFixed(2)}`
                : ""}
            </span>
          </div>
        ),
      },
      {
        id: "targets",
        header: "Applies to",
        minWidth: 180,
        cell: (promotion) => {
          const names = targetNames(promotion, options);
          const hidden = names.length - VISIBLE_TARGETS;

          return (
            <span className="text-sm">
              {names.slice(0, VISIBLE_TARGETS).join(", ")}
              {hidden > 0 ? (
                <span className="text-muted"> +{hidden} more</span>
              ) : null}
            </span>
          );
        },
      },
      {
        id: "code",
        header: "Code",
        width: 170,
        cell: (promotion) =>
          promotion.code ? (
            <div className="flex flex-col leading-tight">
              <span className="font-mono text-sm">{promotion.code}</span>
              <span className="text-muted text-xs tabular-nums">
                {promotion.totalRedemptions}
                {promotion.maxRedemptions !== null
                  ? ` of ${promotion.maxRedemptions}`
                  : ""}{" "}
                used
              </span>
            </div>
          ) : (
            <span className="text-muted text-sm">Automatic</span>
          ),
      },
      {
        id: "schedule",
        header: "Runs",
        width: 210,
        cell: (promotion) => (
          <span className="text-sm tabular-nums">
            {formatDay(promotion.startsAt)} to{" "}
            {promotion.endsAt ? formatDay(promotion.endsAt) : "no end"}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        width: 120,
        cell: (promotion) => {
          const chip = STATUS_CHIP[promotionStatus(promotion, now)];

          return (
            <Chip color={chip.color} variant="soft">
              {chip.label}
            </Chip>
          );
        },
      },
      {
        id: "actions",
        header: () => <span className="sr-only">Actions</span>,
        width: 64,
        align: "end",
        cell: (promotion) => (
          <RowMenu
            items={[
              {
                id: "edit",
                label: "Edit",
                icon: <Pencil className="size-4" />,
                onAction: () => onEdit(promotion),
              },
              {
                id: "delete",
                label: "Delete",
                danger: true,
                icon: <Trash className="size-4" />,
                onAction: () => onDelete(promotion),
              },
            ]}
            label={`Actions for ${promotion.name}`}
          />
        ),
      },
    ];
  }, [options, onEdit, onDelete]);

  return (
    <DataTable
      compact
      columns={columns}
      empty={{
        title: "No promotions yet",
        description: "Add a promotion to discount products or orders.",
      }}
      getRowId={(promotion) => promotion.id}
      isLoading={isLoading}
      label="Promotions"
      pagination={pagination}
      rows={promotions}
    />
  );
};
